import {
  createRouter,
  createWebHistory,
  type NavigationGuard,
  type RouteRecordRaw,
} from 'vue-router'
import { useFamilyContextStore } from '@/stores/familyContext'
import { useOrganizerAuthStore } from '@/stores/organizerAuth'

/**
 * Routes from SPEC §8.1.
 *
 * Every component is a dynamic import, which is what splits the bundle into
 * three entry points (SPEC §13.1). The family route is opened cold, on a phone,
 * from a text message — it must not carry the organizer application with it.
 *
 * The credential-bearing bootstrap routes (`/f/`, `/invite/`, `/access/`, `/e/`)
 * are deliberately absent. They are server-rendered and script-free, because a
 * telemetry SDK loading on them would record a working household credential
 * (SPEC §14.4). If one ever appears in this table, that protection is gone.
 */

declare module 'vue-router' {
  interface RouteMeta {
    /** Requires an established household session. */
    requiresHousehold?: boolean
    /** Requires a signed-in organizer. */
    requiresOrganizer?: boolean
    /** Browser tab title. */
    title?: string
  }
}

const publicRoutes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'landing',
    component: () => import('@/pages/public/LandingPage.vue'),
    meta: { title: "Mabel's Table" },
  },
  {
    path: '/register',
    name: 'register',
    component: () => import('@/pages/public/RegisterPage.vue'),
    meta: { title: 'Start a group' },
  },
  {
    path: '/admin/forgot-password',
    name: 'forgot-password',
    component: () => import('@/pages/public/ForgotPasswordPage.vue'),
    meta: { title: 'Reset your password' },
  },
  {
    // The reset token is not in this URL. The emailed link goes to the API,
    // which puts it in an HttpOnly cookie and redirects here with a status.
    path: '/admin/reset-password',
    name: 'reset-password',
    component: () => import('@/pages/public/ResetPasswordPage.vue'),
    meta: { title: 'Choose a new password' },
  },
  {
    path: '/verify-email',
    name: 'verify-email',
    component: () => import('@/pages/public/VerifyEmailPage.vue'),
    meta: { title: 'Email confirmed' },
  },
  {
    // Where a family member lands from the link sent to their address
    // (MT-182). Public: they may be on a different device with no household
    // session, and /family would bounce them to a sign-in they cannot use.
    path: '/email-confirmed',
    name: 'email-confirmed',
    component: () => import('@/pages/public/EmailConfirmedPage.vue'),
    meta: { title: 'Email confirmed' },
  },
  {
    path: '/admin/sign-in',
    name: 'organizer-sign-in',
    component: () => import('@/pages/public/SignInPage.vue'),
    meta: { title: 'Sign in' },
  },
  {
    // Where /invite/{token} lands, once the server has taken the token out of
    // the URL and put it into a cookie. This route never sees a credential,
    // which is why it is allowed to be an ordinary SPA page (SPEC §14.4).
    path: '/join',
    name: 'join',
    component: () => import('@/pages/public/JoinPage.vue'),
    meta: { title: 'Join your family' },
  },
  {
    // From the foot of a reminder email. The token is only ever posted from a
    // button here; see UnsubscribePage for why the link itself does nothing.
    path: '/unsubscribe',
    name: 'unsubscribe',
    component: () => import('@/pages/public/UnsubscribePage.vue'),
    meta: { title: 'Stop reminders' },
  },
  {
    path: '/recover',
    name: 'recover',
    component: () => import('@/pages/public/RecoverPage.vue'),
    meta: { title: 'Find my family' },
  },
  {
    path: '/privacy',
    name: 'privacy',
    component: () => import('@/pages/public/PrivacyPage.vue'),
    meta: { title: 'Privacy' },
  },
  {
    path: '/terms',
    name: 'terms',
    component: () => import('@/pages/public/TermsPage.vue'),
    meta: { title: 'Terms' },
  },
]

const familyRoutes: RouteRecordRaw[] = [
  {
    // Every family screen shares one shell, so the household name in the bar is
    // defined once rather than repeated per screen (SPEC §8.6.1).
    path: '/family',
    component: () => import('@/layouts/FamilyLayout.vue'),
    meta: { requiresHousehold: true },
    children: [
      {
        path: '',
        name: 'family-home',
        component: () => import('@/pages/family/FamilyHomePage.vue'),
        meta: { title: 'Your family' },
      },
      {
        path: 'events/:eventId',
        name: 'family-event',
        component: () => import('@/pages/family/FamilyEventPage.vue'),
        meta: { title: 'Meal' },
      },
      {
        path: 'history',
        name: 'family-history',
        component: () => import('@/pages/family/FamilyHistoryPage.vue'),
        meta: { title: 'Your meals' },
      },
      {
        path: 'settings',
        name: 'family-settings',
        component: () => import('@/pages/family/FamilySettingsPage.vue'),
        meta: { title: 'Settings' },
      },
    ],
  },
]

const adminRoutes: RouteRecordRaw[] = [
  {
    path: '/admin',
    name: 'organizer-dashboard',
    component: () => import('@/pages/admin/DashboardPage.vue'),
    meta: { requiresOrganizer: true, title: 'Dashboard' },
  },
  {
    path: '/admin/groups/new',
    name: 'create-group',
    component: () => import('@/pages/admin/CreateGroupPage.vue'),
    meta: { requiresOrganizer: true, title: 'Create your group' },
  },
  {
    // Reached from an emailed link, by somebody who may have no account at
    // all -- so no organizer guard. The invitation token is the authority.
    path: '/admin/invitations',
    name: 'accept-invitation',
    component: () => import('@/pages/admin/AcceptInvitationPage.vue'),
    meta: { title: 'Your invitation' },
  },
  {
    // Everything inside a group shares one shell, so the navigation is
    // defined once rather than repeated per screen (SPEC 8.20).
    path: '/admin/groups/:groupId',
    component: () => import('@/layouts/OrganizerLayout.vue'),
    meta: { requiresOrganizer: true },
    children: [
      {
        path: '',
        name: 'group-dashboard',
        component: () => import('@/pages/admin/GroupDashboardPage.vue'),
        meta: { title: 'Dashboard' },
      },
      {
        path: 'events',
        name: 'organizer-events',
        component: () => import('@/pages/admin/EventListPage.vue'),
        meta: { title: 'Meals' },
      },
      {
        // A meal's own address is its readiness, not its menu. SPEC §6.22 asks
        // an organizer to understand whether a meal is ready without opening
        // several pages, and the menu is the page they open from here.
        path: 'events/:eventId',
        name: 'organizer-event',
        component: () => import('@/pages/admin/EventDashboardPage.vue'),
        meta: { title: 'Meal' },
      },
      {
        path: 'events/:eventId/menu',
        name: 'organizer-event-menu',
        component: () => import('@/pages/admin/EventDetailPage.vue'),
        meta: { title: 'Menu' },
      },
      {
        path: 'templates',
        name: 'meal-templates',
        component: () => import('@/pages/admin/MealTemplateListPage.vue'),
        meta: { title: 'Meal templates' },
      },
      {
        path: 'templates/:templateId',
        name: 'meal-template',
        component: () => import('@/pages/admin/MealTemplateEditorPage.vue'),
        meta: { title: 'Meal template' },
      },
      {
        path: 'households',
        name: 'organizer-households',
        component: () => import('@/pages/admin/HouseholdListPage.vue'),
        meta: { title: 'Households' },
      },
      {
        path: 'households/:householdId',
        name: 'organizer-household',
        component: () => import('@/pages/admin/HouseholdDetailPage.vue'),
        meta: { title: 'Household' },
      },
      {
        path: 'organizers',
        name: 'group-organizers',
        component: () => import('@/pages/admin/GroupOrganizersPage.vue'),
        meta: { title: 'Organizers' },
      },
      {
        path: 'audit',
        name: 'group-audit',
        component: () => import('@/pages/admin/AuditLogPage.vue'),
        meta: { title: 'Activity' },
      },
      {
        path: 'reminders',
        name: 'group-reminders',
        component: () => import('@/pages/admin/ReminderSettingsPage.vue'),
        meta: { title: 'Reminders' },
      },
      {
        path: 'settings',
        name: 'group-settings',
        component: () => import('@/pages/admin/GroupSettingsPage.vue'),
        meta: { title: 'Group settings' },
      },
    ],
  },
]

export const routes: RouteRecordRaw[] = [
  ...publicRoutes,
  ...familyRoutes,
  ...adminRoutes,
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/pages/public/NotFoundPage.vue'),
    meta: { title: 'Not found' },
  },
]

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior: (_to, _from, saved) => saved ?? { top: 0 },
})

/**
 * The access guard, exported so it can be tested rather than re-described.
 *
 * It lives here and is applied below. A test that rebuilt this logic against a
 * memory router would pass whether or not the real router had it attached,
 * which is how a guard goes missing without anything turning red — so the spec
 * imports this exact function.
 */
export const accessGuard: NavigationGuard = async (to) => {
  if (to.meta.requiresHousehold) {
    const family = useFamilyContextStore()
    await family.ensureLoaded()

    // `isResolved` is false until household sessions exist (MT-066). Guards are
    // wired and tested now so that enabling them later is one flag rather than
    // a retrofit across every route — the version of this that gets forgotten
    // is the one where the guard is added last.
    if (family.isResolved && !family.hasHousehold) {
      return { name: 'recover' }
    }
  }

  if (to.meta.requiresOrganizer) {
    const auth = useOrganizerAuthStore()
    await auth.ensureLoaded()

    // Unresolved means the server could not be asked, not that nobody is
    // signed in. Redirecting on that would sign people out over a blip.
    if (auth.isResolved && !auth.isSignedIn) {
      return { name: 'organizer-sign-in', query: { returnTo: to.fullPath } }
    }
  }

  return true
}

router.beforeEach(accessGuard)

router.afterEach((to) => {
  document.title = to.meta.title ? `${to.meta.title} · Mabel's Table` : "Mabel's Table"
})
